import React, {ChangeEvent, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {toast} from 'react-toastify';
import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query';
import axios, {AxiosError} from 'axios';
import {QUERY_KEYS} from '../../../utils/queryKeys';
import Card from '../../components/common/Card';
import {type TypedTranslation} from '../../../i18n';
import {deleteCodebook, fetchCodebooks, updateCodebook, uploadCodebook} from '../../../service/codebook.service';
import {ICodebook} from '../../../incase-domain/codebook';
import Button from '../../../incase-domain/components/button/Button';
import DataView, {ViewMode} from '../../components/common/data/DataView';
import ConfirmModal from '../../../incase-domain/components/modals/confirmModal/ConfirmModal';
import InputWithCharacterCounter from '../../../incase-domain/components/input/InputWithCharacterCounter';

const MAX_CODEBOOK_NAME_LENGTH = 64;

function Codebook(): JSX.Element {
    const {t} = useTranslation();
    const queryClient = useQueryClient();
    const fileInputRef = useRef<HTMLInputElement>(null);

    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [selectedForDeletion, setSelectedForDeletion] = useState<ICodebook>({} as ICodebook);

    const [renameModalOpen, setRenameModalOpen] = useState(false);
    const [selectedForRename, setSelectedForRename] = useState<ICodebook>({} as ICodebook);
    const [newName, setNewName] = useState('');

    const {data: codebooks, isFetching, isLoading} = useQuery(QUERY_KEYS.codebook.lists(), () => fetchCodebooks());

    const {mutate: upload} = useMutation(
        (file: File) =>
            uploadCodebook({
                file,
                codebookName: file.name.split('.')[0],
                availableInMultiselect: true,
            }),
        {
            mutationKey: ['upload_codebook'],
            onSuccess: () => {
                queryClient.invalidateQueries({queryKey: QUERY_KEYS.codebook.lists()});
            },
            onError: (error) => handleError(error),
        }
    );

    const {mutate: handleUpdateCodebook} = useMutation((codebook: Partial<ICodebook>) => updateCodebook(codebook), {
        mutationKey: ['update_codebook'],
        onSuccess: () => {
            queryClient.invalidateQueries({queryKey: QUERY_KEYS.codebook.lists()});
            closeRenameModal();
        },
        onError: (error) => handleError(error),
    });

    const {mutate: handleDeleteCodebook} = useMutation((id: string) => deleteCodebook(id), {
        mutationKey: ['delete_codebook'],
        onSuccess: () => {
            queryClient.invalidateQueries({queryKey: QUERY_KEYS.codebook.lists()});
        },
        onError: (error) => handleError(error),
    });

    const handleError = (error: unknown): void => {
        if (axios.isAxiosError(error)) {
            const errorData = (error as AxiosError<{status: number; message: string}>).response?.data;

            if (errorData) {
                toast.error(t(errorData.message as TypedTranslation));
            }
        }
    };

    const openRenameModal = (codebook: ICodebook): void => {
        setSelectedForRename(codebook);
        setNewName(codebook.name);
        setRenameModalOpen(true);
    };

    const closeRenameModal = (): void => {
        setRenameModalOpen(false);
        setSelectedForRename({} as ICodebook);
        setNewName('');
    };

    const onRenameConfirm = (): void => {
        const trimmedName = newName.trim();

        if (!trimmedName) {
            toast.error(t('codebook.nameRequired'));
            return;
        }

        if (trimmedName === selectedForRename.name) {
            closeRenameModal();
            return;
        }

        handleUpdateCodebook({...selectedForRename, name: trimmedName});
    };

    const onSubmit = (e: ChangeEvent<HTMLInputElement>): void => {
        if (e.target.files && e.target.files[0]) {
            upload(e.target.files[0]);
        } else {
            toast.error(t('toasts.importTypeFailed'));
        }
    };

    const renderName = (codebook: ICodebook): JSX.Element => {
        return <>{codebook.name}</>;
    };

    const renderEnabled = (codebook: ICodebook): JSX.Element => {
        return (
            <Button
                leading={codebook.availableInMultiselect ? 'solar:check-square-bold' : 'solar:stop-linear'}
                variant="ghost"
                color={codebook.availableInMultiselect ? 'secondary' : 'primary'}
                square
                size="sm"
                onClick={(e) => {
                    e?.stopPropagation();
                    handleUpdateCodebook({...codebook, availableInMultiselect: !codebook.availableInMultiselect});
                }}
            />
        );
    };

    const renderActions = (codebook: ICodebook): JSX.Element => {
        return (
            <div className="u-flex">
                <Button
                    leading="solar:trash-bin-trash-linear"
                    variant="ghost"
                    color="error"
                    square
                    size="sm"
                    onClick={(e) => {
                        e?.stopPropagation();
                        setSelectedForDeletion(codebook);
                        setDeleteModalOpen(true);
                    }}
                />
            </div>
        );
    };

    return (
        <div className="c-codebook">
            <Card>
                <input className="u-d-none" ref={fileInputRef} type="file" accept=".csv" onChange={onSubmit} />
                <DataView
                    views={[
                        {
                            columns: [
                                {
                                    label: t('codebook.name'),
                                    key: 'themeName',
                                    body: (row) => renderName(row),
                                },
                                {
                                    label: t('codebook.enabled'),
                                    key: 'lastModified',
                                    body: (row) => renderEnabled(row),
                                    width: '11rem',
                                },
                                {
                                    body: (row) => renderActions(row),
                                    width: '4rem',
                                },
                            ],
                            view: ViewMode.TABLE,
                        },
                    ]}
                    rows={codebooks || []}
                    rowIdentifier={(row) => row._id}
                    onRowClick={(row) => openRenameModal(row)}
                    loading={isFetching || isLoading}
                    title={t('codebook.header')}
                    headerActionPrimary={{
                        icon: 'ph:plus-bold',
                        onClick: () => {
                            fileInputRef.current && fileInputRef.current.click();
                        },
                        title: t('genericTable.new'),
                    }}
                    inlineTitle
                />
            </Card>
            <ConfirmModal
                message={t('codebook.confirmDelete', {codebookName: selectedForDeletion.name})}
                color="error"
                isOpen={deleteModalOpen}
                showConfirm
                confirmText={t('common.delete')}
                onConfirm={() => {
                    handleDeleteCodebook(selectedForDeletion._id);
                    setDeleteModalOpen(false);
                }}
                showCancel
                onCancel={() => setDeleteModalOpen(false)}
            />
            <ConfirmModal
                title={t('codebook.rename')}
                icon="solar:pen-linear"
                isOpen={renameModalOpen}
                showConfirm
                confirmText={t('common.save')}
                onConfirm={onRenameConfirm}
                showCancel
                onCancel={closeRenameModal}
            >
                <InputWithCharacterCounter
                    value={newName}
                    onChange={setNewName}
                    placeholder={t('codebook.namePlaceholder')}
                    maxLength={MAX_CODEBOOK_NAME_LENGTH}
                    inlineMaxLength
                    onKeyUp={(e) => {
                        if (e.key === 'Enter') {
                            onRenameConfirm();
                        }
                    }}
                />
            </ConfirmModal>
        </div>
    );
}

export default Codebook;
